import React, { useState, useEffect } from 'react';
import { UserIcon, ChevronDownIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface MemberFilterProps {
  users: any[];
  selectedMember: string;
  onMemberChange: (memberId: string) => void;
  taskCount: number;
}

const MemberFilter: React.FC<MemberFilterProps> = ({
  users,
  selectedMember,
  onMemberChange,
  taskCount
}) => {
  const [isOpen, setIsOpen] = useState(false);

  console.log('🎯 [MemberFilter] Component rendered with props:', {
    usersCount: users.length,
    selectedMember,
    taskCount,
    onMemberChangeType: typeof onMemberChange
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Only close if clicking outside the dropdown container
      if (isOpen && !target.closest('.member-filter-dropdown')) {
        console.log('🔄 [MemberFilter] Closing dropdown due to outside click');
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMemberSelect = (event: React.MouseEvent, memberId: string) => {
    event.preventDefault();
    event.stopPropagation();

    console.log('🔄 [MemberFilter] Selecting member:', memberId);
    console.log('🔄 [MemberFilter] Current selectedMember prop:', selectedMember);
    console.log('🔄 [MemberFilter] onMemberChange callback:', typeof onMemberChange);

    setIsOpen(false);

    console.log('🚀 [MemberFilter] Calling onMemberChange with:', memberId);
    onMemberChange(memberId);
    console.log('✅ [MemberFilter] onMemberChange called');
  };

  const getSelectedMemberName = () => {
    if (selectedMember === 'all') {
      return 'All Members';
    }
    const user = users.find(u => u.id === selectedMember);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
  };

  return (
    <div className="flex flex-col space-y-4">
      {/* Member Dropdown */}
      <div className="relative member-filter-dropdown">
        <button
          onClick={() => {
            console.log('🔄 [MemberFilter] Dropdown button clicked, current isOpen:', isOpen);
            setIsOpen(!isOpen);
            console.log('🔄 [MemberFilter] Setting isOpen to:', !isOpen);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[200px]"
        >
          <UserIcon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 flex-1 text-left">
            {getSelectedMemberName()}
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="p-2">
              <button
                onClick={(e) => {
                  console.log('🔄 [MemberFilter] All Members button clicked');
                  handleMemberSelect(e, 'all');
                }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  selectedMember === 'all'
                    ? 'bg-primary-100 text-primary-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <UserGroupIcon className="w-4 h-4" />
                  <span>All Members</span>
                </div>
              </button>
              
              <div className="border-t border-gray-200 my-2" />
              
              {users.map((user) => (
                <button
                  key={user.id}
                  onClick={(e) => {
                    console.log('🔄 [MemberFilter] User button clicked for:', user.firstName, user.lastName);
                    handleMemberSelect(e, user.id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    selectedMember === user.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </span>
                    </div>
                    <span>{user.firstName} {user.lastName}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <UserIcon className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Currently viewing: {getSelectedMemberName()} -
            </span>
          </div>
          <div className="text-xs text-blue-600">
             {taskCount} tasks found
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberFilter;
